/**
 * 全局主题切换 Hook
 * 支持 dark / light 模式，持久化到 localStorage
 */

import { useState, useEffect, useCallback } from 'react';
import { getStorageItem, setStorageItem, StorageKeys } from '@/utils/storage';

export type Theme = 'dark' | 'light';

function getSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

/**
 * 全局主题 Hook
 * - 从 localStorage 读取用户偏好
 * - 无偏好时跟随系统
 * - 切换时持久化并更新 data-theme 属性
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    return getStorageItem<Theme>(StorageKeys.THEME, getSystemTheme());
  });

  // 初始化时应用主题
  useEffect(() => {
    applyTheme(theme);
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    setStorageItem(StorageKeys.THEME, newTheme);
    applyTheme(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return { theme, setTheme, toggleTheme };
}
